<!-- SystemPromptImportDialog.vue -->
<template>
  <v-dialog v-model="internalDialog" max-width="800px">
    <v-card class="system-prompt-import-card">
      <DialogTitleBar>
        <span>시스템 프롬프트 가져오기</span>
        <template #actions>
          <v-btn icon variant="text" aria-label="닫기" @click="closeDialog">
            <svg-icon type="mdi" :path="icons.mdiClose" />
          </v-btn>
        </template>
      </DialogTitleBar>

      <v-card-text class="system-prompt-import-body">
        <p class="system-prompt-import-description">
          아래 목록에서 프롬프트(설정)를 선택해 가져올 수 있습니다.<br>
          <strong>시스템 프롬프트와 OpenAI 파라미터 가져오기</strong> (금지어는 제외)
        </p>

        <v-divider class="mb-2" />

        <v-tabs v-model="tab" color="primary" grow>
          <v-tab :value="0">공유 받은 설정</v-tab>
          <v-tab :value="1">넣어둔 설정</v-tab>
        </v-tabs>

        <v-divider class="mt-0" />

        <v-tabs-window v-model="tab" class="mt-2">
          <v-tabs-window-item :value="0">
            <v-list>
              <v-list-item
                v-for="(prompt, index) in sharedPrompts"
                :key="index"
                @click="selectSharedPrompt(index)"
                :class="{ 'selected-item': selectedSharedIndex === index }"
                rounded="lg"
              >
                <div class="single-line-content">
                  <span class="prompt-title">{{ prompt.name }}</span>
                  <span class="prompt-description">{{ prompt.description || '(설명 없음)' }}</span>
                </div>
              </v-list-item>
            </v-list>
          </v-tabs-window-item>

          <v-tabs-window-item :value="1">
            <v-list>
              <v-list-item
                v-for="(prompt, index) in myPrompts"
                :key="prompt.id"
                @click="selectMyPrompt(index)"
                :class="{ 'selected-item': selectedMyIndex === index }"
                rounded="lg"
              >
                <div class="single-line-content">
                  <span class="prompt-title">{{ prompt.title }}</span>
                  <span class="prompt-description">{{ prompt.description || '(설명 없음)' }}</span>
                </div>
                <template #append>
                  <v-btn icon variant="text" aria-label="삭제" @click.stop="deleteMyPrompt(index)">
                    <svg-icon type="mdi" :path="icons.mdiDelete" color="red" />
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" @click="applySelectedPrompt">
          가져오기
        </v-btn>
        <v-btn variant="text" @click="closeDialog">닫기</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="showConfirmDialog" max-width="420px">
      <v-card class="import-confirm-card">
        <DialogTitleBar>
          <span>확인</span>
          <template #actions>
            <v-btn icon variant="text" aria-label="닫기" @click="confirmImport">
              <svg-icon type="mdi" :path="icons.mdiClose" />
            </v-btn>
          </template>
        </DialogTitleBar>
        <v-card-text class="import-confirm-body">
          가져온 설정을 적용하려면 상단의 저장 버튼을 클릭하세요.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="confirmImport">
            확인
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { VTabsWindow, VTabsWindowItem } from 'vuetify/components';
import DialogTitleBar from '@/components/DialogTitleBar.vue';
import icons from '@/icons';
import { sharedPrompts } from '@/sharedPrompts';
import { db } from '@/firebase';
import { collection, query, getDocs, doc, deleteDoc } from 'firebase/firestore';

export default {
  name: 'SystemPromptImportDialog',
  components: {
    DialogTitleBar,
    VTabsWindow,
    VTabsWindowItem,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue', 'applyPrompt', 'showSnackbar'],
  data() {
    return {
      icons,
      internalDialog: false,
      tab: 0,
      selectedSharedIndex: null,
      selectedMyIndex: null,
      sharedPrompts,
      myPrompts: [],
      showConfirmDialog: false,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.internalDialog = newVal;
        if (newVal) {
          this.loadMyPrompts();
        }
      },
    },
    internalDialog(newVal) {
      this.$emit('update:modelValue', newVal);
    },
  },
  methods: {
    closeDialog() {
      this.internalDialog = false;
    },
    async loadMyPrompts() {
      if (!this.userId) {
        console.error('사용자 ID가 없습니다.');
        return;
      }
      try {
        const userStoredPromptsRef = collection(db, 'users', this.userId, 'storedPrompts');
        const q = query(userStoredPromptsRef);
        const querySnapshot = await getDocs(q);
        this.myPrompts = querySnapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }));
      } catch (error) {
        console.error('내가 넣어둔 프롬프트를 불러오는 중 오류 발생:', error);
        this.$emit('showSnackbar', '프롬프트를 불러오는 중 오류가 발생했습니다.', 'error');
      }
    },
    async deleteMyPrompt(index) {
      const prompt = this.myPrompts[index];
      if (!prompt || !prompt.id) {
        this.$emit('showSnackbar', '삭제할 프롬프트가 없습니다.', 'error');
        return;
      }

      try {
        const promptDocRef = doc(db, 'users', this.userId, 'storedPrompts', prompt.id);
        await deleteDoc(promptDocRef);
        this.myPrompts.splice(index, 1);
        this.$emit('showSnackbar', '프롬프트가 삭제되었습니다.', 'success');
      } catch (error) {
        console.error('프롬프트 삭제 실패:', error);
        this.$emit('showSnackbar', '프롬프트 삭제에 실패했습니다.', 'error');
      }
    },
    selectSharedPrompt(index) {
      if (this.selectedSharedIndex === index) {
        this.selectedSharedIndex = null;
      } else {
        this.selectedSharedIndex = index;
        this.selectedMyIndex = null;
      }
    },
    selectMyPrompt(index) {
      if (this.selectedMyIndex === index) {
        this.selectedMyIndex = null;
      } else {
        this.selectedMyIndex = index;
        this.selectedSharedIndex = null;
      }
    },
    applySelectedPrompt() {
      let selectedPrompt = null;

      if (this.tab === 0 && this.selectedSharedIndex !== null) {
        selectedPrompt = this.sharedPrompts[this.selectedSharedIndex].settings;
      } else if (this.tab === 1 && this.selectedMyIndex !== null) {
        selectedPrompt = this.myPrompts[this.selectedMyIndex].settings;
      }

      if (selectedPrompt === null) {
        this.$emit('showSnackbar', '프롬프트를 선택해주세요.', 'warning');
        return;
      }

      const updatedPrompt = {
        ...selectedPrompt,
        openAIParameters: {
          ...selectedPrompt.openAIParameters,
          seed: selectedPrompt.openAIParameters?.seed ?? 1128585432,
          randomSeed: selectedPrompt.openAIParameters?.randomSeed ?? true,
        },
      };

      this.$emit('applyPrompt', updatedPrompt);
      this.showConfirmDialog = true;
      this.internalDialog = false;
    },
    confirmImport() {
      this.showConfirmDialog = false;
    },
  },
};
</script>

<style scoped>
.v-tabs {
  margin-top: 16px;
}

.system-prompt-import-body {
  padding-top: 6px;
}

.system-prompt-import-description {
  margin-bottom: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.64);
}

.system-prompt-import-description :deep(strong) {
  color: rgba(0, 0, 0, 0.8);
  font-weight: 700;
}

.selected-item {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.v-divider {
  margin: 16px 0;
}

.single-line-content {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
}

.prompt-title {
  font-size: 1rem !important;
  font-weight: bold;
  margin-right: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.prompt-description {
  font-size: 1rem !important;
  color: rgba(0, 0, 0, 0.58);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 2;
}

.import-confirm-body {
  padding-top: 6px;
  font-size: 0.94rem;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.64);
}

.v-card-text,
.v-card-actions {
  padding: 16px;
}

@media (max-width: 600px) {
  .prompt-title {
    font-size: 14px;
  }

  .prompt-description {
    font-size: 12px;
  }
}

@media (min-width: 601px) {
  .prompt-title {
    font-size: 16px;
  }

  .prompt-description {
    font-size: 14px;
  }
}
</style>
